import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUp, ArrowDown, X, Search, Star } from 'lucide-react';
import { apiFetch } from './api';
import { getHome } from './homeApi';
import HomeActions from './HomeActions';
import { formatarPreco } from '../data/produtos';

export default function Destaques() {
  const { data: homeData, isLoading } = useQuery({ queryKey: ['admin-home'], queryFn: getHome });
  const [cfg, setCfg] = useState(null);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    if (homeData) setCfg(JSON.parse(JSON.stringify(homeData.rascunho)));
  }, [homeData]);

  const { data: produtosData } = useQuery({
    queryKey: ['admin-produtos-picker', busca],
    queryFn: async () => (await apiFetch(`/admin/produtos?q=${encodeURIComponent(busca)}&limit=50`)).json(),
  });

  const destaques = cfg?.destaques;
  const secao = cfg?.secoes.find((s) => s.id === 'destaques');
  const selecionados = useMemo(() => {
    if (!cfg || !produtosData) return [];
    const todos = produtosData.items;
    const porId = Object.fromEntries(todos.map((p) => [p.id, p]));
    return cfg.destaques.ids.map((id) => porId[id] || { id, nome: id, fotos: [], precoVenda: 0 });
  }, [cfg, produtosData]);

  if (isLoading || !cfg) return <p className="text-sm text-slate-400">Carregando...</p>;

  const setDest = (parcial) => setCfg({ ...cfg, destaques: { ...destaques, ...parcial } });
  const setSecao = (parcial) =>
    setCfg({ ...cfg, secoes: cfg.secoes.map((s) => (s.id === 'destaques' ? { ...s, ...parcial } : s)) });

  const alternarProduto = (p) => {
    const ids = destaques.ids.includes(p.id)
      ? destaques.ids.filter((i) => i !== p.id)
      : [...destaques.ids, p.id];
    setDest({ ids });
  };

  const mover = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= destaques.ids.length) return;
    const ids = [...destaques.ids];
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setDest({ ids });
  };

  const inputCls = 'mt-1 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-mk-blue focus:ring-2 focus:ring-mk-blue/20';
  const labelCls = 'text-xs font-bold uppercase tracking-wide text-slate-500';

  return (
    <div data-testid="admin-destaques" className="max-w-3xl">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">Produtos em destaque</h1>
      <p className="mt-1 text-sm text-slate-500">Escolha quais produtos aparecem na seção de destaques da página inicial.</p>

      <div className="mt-5 space-y-4 rounded-2xl bg-white p-5 shadow-sm">
        <label className="flex items-center gap-3">
          <input data-testid="destaques-ativo" type="checkbox" checked={destaques.ativo && secao.ativo} onChange={(e) => { setDest({ ativo: e.target.checked }); setSecao({ ativo: e.target.checked }); }} className="h-5 w-5 accent-mk-blue" />
          <span className="text-sm font-bold text-slate-800">Exibir seção de destaques na página inicial</span>
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Título da seção</label>
            <input data-testid="destaques-titulo" value={secao.titulo} onChange={(e) => setSecao({ titulo: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Subtítulo</label>
            <input data-testid="destaques-subtitulo" value={secao.subtitulo} onChange={(e) => setSecao({ subtitulo: e.target.value })} className={inputCls} />
          </div>
        </div>
        <div className="max-w-[200px]">
          <label className={labelCls}>Quantidade máxima exibida</label>
          <input data-testid="destaques-limite" type="number" min="1" max="24" value={destaques.limite} onChange={(e) => setDest({ limite: parseInt(e.target.value, 10) || 8 })} className={inputCls} />
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm">
        <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-slate-900">
          <Star size={17} className="text-amber-500" /> Selecionados ({destaques.ids.length})
        </h2>
        {selecionados.length === 0 && <p className="mt-2 text-sm text-slate-400">Nenhum produto em destaque ainda.</p>}
        <div className="mt-3 space-y-2">
          {selecionados.map((p, i) => (
            <div key={p.id} data-testid={`destaque-sel-${p.id}`} className="flex items-center gap-3 rounded-xl bg-slate-50 p-2.5">
              {p.fotos?.[0] && <img src={p.fotos[0]} alt="" className="h-10 w-10 rounded-lg object-cover" />}
              <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">{p.nome}</span>
              <span className="text-xs font-bold text-mk-blue">{p.precoVenda ? formatarPreco(p.precoPromocional ?? p.precoVenda) : ''}</span>
              <button data-testid={`destaque-up-${i}`} onClick={() => mover(i, -1)} disabled={i === 0} aria-label="Subir" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 disabled:opacity-30"><ArrowUp size={15} /></button>
              <button data-testid={`destaque-down-${i}`} onClick={() => mover(i, 1)} disabled={i === destaques.ids.length - 1} aria-label="Descer" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 disabled:opacity-30"><ArrowDown size={15} /></button>
              <button data-testid={`destaque-remove-${p.id}`} onClick={() => alternarProduto(p)} aria-label="Remover" className="rounded-lg p-1.5 text-mk-red hover:bg-red-50"><X size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm">
        <label className={labelCls}>Adicionar produtos</label>
        <div className="relative mt-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="destaques-search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto por nome..." className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-mk-blue" />
        </div>
        <div className="mt-3 max-h-72 space-y-1.5 overflow-y-auto">
          {(produtosData?.items || []).map((p) => {
            const marcado = destaques.ids.includes(p.id);
            return (
              <label key={p.id} data-testid={`destaque-pick-${p.id}`} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-2.5 transition-colors ${marcado ? 'border-mk-blue bg-blue-50' : 'border-slate-100 hover:bg-slate-50'}`}>
                <input type="checkbox" checked={marcado} onChange={() => alternarProduto(p)} className="h-4 w-4 accent-mk-blue" />
                {p.fotos?.[0] && <img src={p.fotos[0]} alt="" className="h-9 w-9 rounded-lg object-cover" />}
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-700">{p.nome}</span>
                {p.status !== 'ativo' && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-extrabold text-slate-500">{p.status}</span>}
              </label>
            );
          })}
        </div>
      </div>

      <HomeActions cfg={cfg} temAlteracoes={homeData?.temAlteracoes} />
    </div>
  );
}
